import { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

function OrderForm() {
  // State to store form data
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    product: 'Fertile Eggs',
    quantity: '',
    message: ''
  });

  // Status: 'idle' | 'saving' | 'success' | 'error'
  const [status, setStatus] = useState('idle');

  // Updates state when user types
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Opens WhatsApp with pre-filled message
  const openWhatsApp = () => {
    const whatsappMessage = `Hello Motif Farms!%0A%0A*New Order Inquiry*%0A%0AName: ${formData.name}%0APhone: ${formData.phone}%0AProduct: ${formData.product}%0AQuantity: ${formData.quantity}%0AMessage: ${formData.message}`;
    window.open(`https://wa.me/233557455228?text=${whatsappMessage}`, '_blank');
  };

  // Handles form submission - saves to Firestore then opens WhatsApp
  const handleSubmit = (e) => {
    e.preventDefault();

    // Report conversion to Google Ads
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17997378875/arEQCL21kYwcELvq6IVD',
        'transaction_id': ''
      });
    }

    // Open WhatsApp immediately — never block the customer
    openWhatsApp();

    // Save to Firestore in the background
    setStatus('saving');

    // Add a timeout so we don't hang forever if Firestore is unreachable
    const savePromise = addDoc(collection(db, 'orders'), {
      ...formData,
      status: 'new',
      createdAt: serverTimestamp()
    });

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Firestore timeout')), 10000)
    );

    Promise.race([savePromise, timeoutPromise])
      .then(() => {
        setStatus('success');
        // Reset form after a short delay
        setTimeout(() => {
          setFormData({
            name: '',
            phone: '',
            product: 'Fertile Eggs',
            quantity: '',
            message: ''
          });
          setStatus('idle');
        }, 4000);
      })
      .catch((error) => {
        console.error('Error saving order:', error);
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      });
  };

  return (
    <section className="py-24 px-6 bg-background-light dark:bg-background-dark border-t border-gray-100 dark:border-gray-800" id="order">
      <div className="max-w-[1100px] mx-auto">
        <div className="bg-white dark:bg-background-dark rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-gray-100 dark:border-gray-800">

          {/* Left Side - Contact Info */}
          <div className="md:w-1/3 bg-primary p-6 md:p-12 text-white flex flex-col justify-between">
            <div>
              <h3 className="text-3xl font-bold mb-6">Place Your Order</h3>
              <p className="text-white/95 mb-8 leading-relaxed font-medium">
                Fill out the form to inquire about our stock. Our sales team will contact you within 24 hours.
              </p>

              {/* Contact Details */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined">call</span>
                  <span>055 745 5228</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined">location_on</span>
                  <span>Medie–Kotoku, Accra, Ghana</span>
                </div>
                <div className="flex items-start gap-4">
                  <span className="material-symbols-outlined flex-shrink-0">mail</span>
                  <span className="break-all">motifinvestmentsghana@gmail.com</span>
                </div>
              </div>
            </div>

            {/* WhatsApp Button */}
            <div className="mt-12">
              <a
                className="flex items-center justify-center gap-3 bg-white text-primary font-bold py-4 rounded-xl hover:bg-cream transition-all w-full"
                href="https://wa.me/233557455228"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                WhatsApp Inquiry
              </a>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="flex-1 p-6 md:p-12">

            {/* Success Banner */}
            {status === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <span className="material-symbols-outlined text-green-600">check_circle</span>
                <p className="text-green-800 font-medium text-sm">Order recorded successfully! WhatsApp has been opened.</p>
              </div>
            )}

            {/* Error Banner */}
            {status === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <span className="material-symbols-outlined text-red-600">error</span>
                <p className="text-red-800 font-medium text-sm">Couldn't save the order, but WhatsApp has been opened. Please try again later.</p>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>

              {/* Name & Phone Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-primary focus:border-primary px-4 py-3"
                    placeholder="Your Name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-primary focus:border-primary px-4 py-3"
                    placeholder="+233..."
                    required
                  />
                </div>
              </div>

              {/* Product & Quantity Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                    Select Product
                  </label>
                  <select
                    name="product"
                    value={formData.product}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-primary focus:border-primary px-4 py-3"
                    required
                  >
                    <option value="Fertile Eggs">Fertile Eggs</option>
                    <option value="Day-Old Poults">Day-Old Poults</option>
                    <option value="Growing Poults">Growing Poults</option>
                    <option value="Live Birds">Live Birds</option>
                    <option value="Turkey Meat">Turkey Meat</option>
                    <option value="Poultry Feed">Poultry Feed</option>
                    <option value="Other">Other Inquiry</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                    Quantity
                  </label>
                  <input
                    type="text"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-primary focus:border-primary px-4 py-3"
                    placeholder="e.g., 10 eggs, 5 birds"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-primary focus:border-primary px-4 py-3"
                  placeholder="Special requirements or questions..."
                  rows="4"
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={status === 'saving'}
                className={`w-full text-white py-4 rounded-lg font-bold text-lg shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2 ${status === 'saving'
                  ? 'bg-primary/60 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary-light'
                  }`}
              >
                {status === 'saving' ? (
                  <>
                    <span className="material-symbols-outlined animate-spin">progress_activity</span>
                    Saving...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">send</span>
                    Send Inquiry
                  </>
                )}
              </button>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}

export default OrderForm;